import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  Chip,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { AgronodTypography } from "@agronod/mui-components";
import { AdminService } from "@/services/admin.service";
import { UserRole, CreateUserPayload } from "@/types";
import { enqueueSnackbar } from "notistack";

// Konstanter för Swedish regioner
const SWEDISH_REGIONS = [
  "Blekinge",
  "Dalarna",
  "Gotland",
  "Gävleborg",
  "Halland",
  "Jämtland",
  "Jönköping",
  "Kalmar",
  "Kronoberg",
  "Norrbotten",
  "Skåne",
  "Stockholm",
  "Södermanland",
  "Uppsala",
  "Värmland",
  "Västerbotten",
  "Västernorrland",
  "Västmanland",
  "Västra Götaland",
  "Örebro",
  "Östergötland",
];

// Vanliga produktionstyper
const PRODUCTION_TYPES = [
  "Ekologisk",
  "Konventionell",
  "Friland",
  "Växthus",
  "Djurhållning",
  "Växtodling",
];

// Vanliga certifieringar
const CERTIFICATIONS = [
  "KRAV",
  "EU-Eko",
  "Demeter",
  "Sigill",
  "Svenskt Sigill",
  "MSC",
  "ASC",
];

const AdminCreateUserPage = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState<CreateUserPayload>({
    email: "",
    password: "",
    role: UserRole.AGROKOLL_OWNER,
    isActive: true,
    farmName: "",
    organizationNumber: "",
    region: "",
    productionTypes: [],
    certifications: [],
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors["email"] = "E-postadress krävs";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors["email"] = "Ogiltig e-postadress";
    }

    if (!formData.password) {
      newErrors["password"] = "Lösenord krävs";
    } else if (formData.password.length < 8) {
      newErrors["password"] = "Lösenordet måste vara minst 8 tecken långt";
    }

    if (!formData.farmName) {
      newErrors["farmName"] = "Gårdsnamn krävs";
    }

    if (!formData.region) {
      newErrors["region"] = "Region krävs";
    }

    if (
      formData.organizationNumber &&
      !/^\d{6}-\d{4}$/.test(formData.organizationNumber)
    ) {
      newErrors["organizationNumber"] =
        "Organisationsnummer måste ha formatet XXXXXX-XXXX";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      await AdminService.createUser(formData);
      enqueueSnackbar(`Användare ${formData.email} har skapats!`, {
        variant: "success",
      });
      navigate("/admin/users");
    } catch (err: any) {
      console.error("Failed to create user:", err);
      setError(err.message || "Kunde inte skapa användare. Försök igen.");
    } finally {
      setLoading(false);
    }
  };

  // Copy password to clipboard
  const handleCopyPassword = () => {
    navigator.clipboard.writeText(formData.password);
    enqueueSnackbar("Lösenord kopierat!", { variant: "success" });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/users")}
          sx={{ mb: 2 }}
        >
          Tillbaka till användare
        </Button>
        <AgronodTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Skapa ny användare
        </AgronodTypography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Fyll i användarens uppgifter nedan. Lösenordet måste meddelas
          användaren manuellt.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{ p: 4, border: "1px solid", borderColor: "divider" }}
      >
        <form onSubmit={handleSubmit}>
          {/* Error alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* ANVÄNDARKONTO */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Användarkonto
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
            <TextField
              fullWidth
              label="E-postadress"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              error={Boolean(errors["email"])}
              helperText={errors["email"]}
              required
            />

            <TextField
              fullWidth
              label="Lösenord"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              error={Boolean(errors["password"])}
              helperText={errors["password"] || "Minst 8 tecken"}
              required
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                      {formData.password && (
                        <IconButton onClick={handleCopyPassword} edge="end">
                          <CopyIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Roll</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value as UserRole })
                }
                label="Roll"
              >
                <MenuItem value={UserRole.AGROKOLL_OWNER}>
                  ÄGARE - Kontoägare
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_ADMIN}>
                  ADMIN - Kontoadministratör
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_SYSTEM_ADMIN}>
                  CONTROL_ADMIN - Kan hantera kontrolltyper
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_SYSTEM_ADMIN}>
                  SYSTEM_ADMIN - Full systemadministration
                </MenuItem>
                <MenuItem value={UserRole.AGROKOLL_SYSTEM_ADMIN}>
                  SUPER_ADMIN - Allt + användarhantering
                </MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive === true}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label="Aktiv användare"
            />
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* GÅRDSPROFIL */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Gårdsprofil
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Gårdsnamn"
              value={formData.farmName}
              onChange={(e) =>
                setFormData({ ...formData, farmName: e.target.value })
              }
              error={Boolean(errors["farmName"])}
              helperText={errors["farmName"]}
              required
            />

            <TextField
              fullWidth
              label="Organisationsnummer"
              value={formData.organizationNumber}
              onChange={(e) =>
                setFormData({ ...formData, organizationNumber: e.target.value })
              }
              error={Boolean(errors["organizationNumber"])}
              helperText={
                errors["organizationNumber"] || "Format: XXXXXX-XXXX (valfritt)"
              }
              placeholder="123456-7890"
            />

            <Autocomplete
              fullWidth
              options={SWEDISH_REGIONS}
              value={formData.region}
              onChange={(_, value) =>
                setFormData({ ...formData, region: value || "" })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Region"
                  required
                  error={Boolean(errors["region"])}
                  helperText={errors["region"]}
                />
              )}
            />

            <Autocomplete
              multiple
              fullWidth
              options={PRODUCTION_TYPES}
              value={formData.productionTypes}
              onChange={(_, value) =>
                setFormData({ ...formData, productionTypes: value })
              }
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getItemProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Produktionstyper"
                  placeholder="Välj eller skriv..."
                />
              )}
              freeSolo
            />

            <Autocomplete
              multiple
              fullWidth
              options={CERTIFICATIONS}
              value={formData.certifications}
              onChange={(_, value) =>
                setFormData({ ...formData, certifications: value })
              }
              renderValue={(value, getItemProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getItemProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Certifieringar"
                  placeholder="Välj eller skriv..."
                />
              )}
              freeSolo
            />
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
            >
              {loading ? "Skapar..." : "Skapa användare"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/admin/users")}
              disabled={loading}
              fullWidth
            >
              Avbryt
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default AdminCreateUserPage;
